import { StrictMode } from "react";
import { createRoot } from "react-dom/client";
import { RouterProvider, createRouter } from "@tanstack/react-router";
import { QueryClient } from "@tanstack/react-query";

import "./globals.css";

// Cargado de fuentes Geist (antes venía vía next/font) · usamos los paquetes
// fontsource que inyectan @font-face standalone, sin depender de Next.
import "@fontsource-variable/geist";
import "@fontsource-variable/geist-mono";

// dayjs locale español · DatesProvider lo consume, pero la importación de
// efectos secundarios debe vivir aquí en el bootstrap.
import dayjs from "dayjs";
import "dayjs/locale/es";
dayjs.locale("es");

import { routeTree } from "./routeTree.gen";

// Tailwind: aplica la clase `dark` en <html> según la preferencia guardada
// (el script inline de index.html ya lo hizo una vez antes de este punto,
// para no parpadear · esto solo deja el listener de "Sistema" activo).
import { watchSystemTheme } from "./lib/theme-store";
watchSystemTheme();

/**
 * El QueryClient vive a nivel de módulo para que esté disponible TANTO
 * como contexto del router (para loaders / route guards con
 * `ensureQueryData`) COMO para el `<QueryClientProvider>` en `__root.tsx`.
 * Una sola instancia compartida garantiza cache coherente entre los dos
 * mundos.
 */
export const queryClient = new QueryClient({
  defaultOptions: {
    queries: { staleTime: 60_000, retry: 1 },
    mutations: { retry: 0 },
  },
});

const router = createRouter({
  routeTree,
  context: { queryClient },
  defaultPreload: "intent",
  defaultPendingMs: 300,
  defaultPendingMinMs: 500,
  scrollRestoration: true,
});

// Type-safety para hooks como `useNavigate`, `useParams`, `Link` · registramos
// el router en el namespace de TanStack para que los path/search/params
// queden inferidos en toda la app.
declare module "@tanstack/react-router" {
  interface Register {
    router: typeof router;
  }
}

const rootEl = document.getElementById("root");
if (!rootEl) {
  throw new Error(
    "No se encontró #root en index.html · revisa la plantilla raíz de Vite."
  );
}

createRoot(rootEl).render(
  <StrictMode>
    <RouterProvider router={router} />
  </StrictMode>
);
